/**
 * One resident's full history: a vertical timeline per domain lane, and where
 * each record sits now. Owners come from recorded facts only, so an unknown
 * owner reads "not recorded" - never blank, never a guess.
 */
import React, { useMemo } from 'react';
import { Drawer } from '../bd/bdUi';
import { Domain, Step, Timeline, flowApi } from './flowApi';
import { DOMAINS, DOMAIN_COLOR, DOMAIN_LABEL, ORIGIN_LABEL } from './chartTheme';
import { DomainMark, NOT_RECORDED, OriginPill, Remote, fmtStepWhen, fmtWhen, stateLabel, useRemote } from './flowUi';
import { STATUS } from '../../../components/tide';

export function actorLabel(s: Pick<Step, 'actorName' | 'actorUserId' | 'origin'>): string {
  if (s.actorName) return s.actorName;
  if (s.origin === 'SYSTEM') return 'Automation';
  if (s.origin === 'IMPORT') return 'Monday import';
  return s.actorUserId ? 'Unnamed user' : NOT_RECORDED;
}

export const landedWith = (s: Pick<Step, 'ownerAfterName' | 'ownerAfterUserId'>) =>
  s.ownerAfterName || (s.ownerAfterUserId ? 'Unnamed user' : NOT_RECORDED);

export default function TimelineDrawer({ patientId, label, onClose }: { patientId: string | null; label?: string | null; onClose: () => void }) {
  return (
    <Drawer open={!!patientId} onClose={onClose} title={label || 'Resident timeline'} description="Every recorded movement, grouped by workflow.">
      {patientId && <TimelineBody patientId={patientId} />}
    </Drawer>
  );
}

function TimelineBody({ patientId }: { patientId: string }) {
  const { state, retry } = useRemote<Timeline>(`timeline|${patientId}`, () => flowApi.timeline(patientId));
  return (
    <div className="tide px-4 py-4">
      <Remote state={state} retry={retry} skeleton="lines" reportFreshness={false}>{(data) => <TimelineContent data={data} />}</Remote>
    </div>
  );
}

const sectionTitle: React.CSSProperties = { margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--tide-text-13)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--tide-ink-2)' };

function TimelineContent({ data }: { data: Timeline }) {
  const openNow = data.current.filter((c) => c.open);
  const closed = data.current.filter((c) => !c.open);
  // An undated step has no place on a timeline, so it is listed apart, after the dated ones.
  const dated = useMemo(() => data.steps.filter((s) => s.timeKnown !== false), [data]);
  const undated = useMemo(() => data.steps.filter((s) => s.timeKnown === false), [data]);
  const lanes = useMemo(
    () => DOMAINS.map((d) => ({ domain: d, steps: dated.filter((s) => s.domain === d) })).filter((l) => l.steps.length > 0),
    [dated],
  );
  return (
    <div className="space-y-6">
      <p className="tide-small tide-ink-2" style={{ margin: 0 }}>
        {data.resident.label}{data.resident.facilityName ? ` · ${data.resident.facilityName}` : ''}
      </p>

      <section aria-label="Where it is now">
        <h3 style={sectionTitle}>Where it is now</h3>
        {openNow.length === 0 ? <p className="tide-small tide-ink-2" style={{ margin: 0 }}>No open records for this resident.</p> : <CurrentList items={openNow} />}
      </section>

      {closed.length > 0 && (
        <section aria-label="Closed">
          <h3 style={sectionTitle}>Closed</h3>
          <CurrentList items={closed} />
        </section>
      )}

      {lanes.length === 0 && undated.length === 0 && <p className="tide-small tide-ink-2" style={{ margin: 0 }}>No movement has been recorded for this resident.</p>}
      {lanes.map((lane) => (
        <section key={lane.domain} aria-label={`${DOMAIN_LABEL[lane.domain as Domain]} timeline`}>
          <h3 style={{ ...sectionTitle, color: 'var(--tide-ink)' }}>
            <DomainMark domain={lane.domain} size={12} />{DOMAIN_LABEL[lane.domain]}
            <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--tide-ink-2)' }}>{lane.steps.length} {lane.steps.length === 1 ? 'step' : 'steps'}</span>
          </h3>
          <ol className="pl-5" style={{ borderLeft: `3px solid ${DOMAIN_COLOR[lane.domain]}`, listStyle: 'none', margin: '0 0 0 6px' }}>
            {lane.steps.map((s) => <StepItem key={s.id} s={s} />)}
          </ol>
        </section>
      ))}

      {undated.length > 0 && (
        <section aria-label="Undated (imported from Monday)">
          <h3 style={sectionTitle}>Undated (imported from Monday) <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>{undated.length} {undated.length === 1 ? 'step' : 'steps'}</span></h3>
          <p className="tide-tiny" style={{ margin: '0 0 8px', color: STATUS.missing.fg }}>The source never recorded when these happened.</p>
          <ol className="pl-5" style={{ borderLeft: `3px dashed ${STATUS.missing.fg}`, listStyle: 'none', margin: '0 0 0 6px' }}>
            {undated.map((s) => <StepItem key={s.id} s={s} showDomain />)}
          </ol>
        </section>
      )}
    </div>
  );
}

function StepItem({ s, showDomain = false }: { s: Step; showDomain?: boolean }) {
  return (
    <li className="relative pb-4 last:pb-0" data-testid="timeline-step">
      <span aria-hidden className="absolute" style={{ left: -29, top: 4, background: 'var(--tide-surface)', borderRadius: 999, padding: 2, lineHeight: 0 }}>
        <DomainMark domain={s.domain} size={11} />
      </span>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 tide-tiny tide-ink-2">
        {s.timeKnown === false ? <span>{fmtStepWhen(s)}</span> : <time dateTime={s.at}>{fmtStepWhen(s)}</time>}
        {showDomain && <span className="inline-flex items-center gap-1"><DomainMark domain={s.domain} />{DOMAIN_LABEL[s.domain]}</span>}
        <span style={{ border: '1px solid var(--tide-line)', borderRadius: 4, padding: '0 6px', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 12 }}>{s.kind.toLowerCase()}</span>
        <OriginPill origin={s.origin} />
      </div>
      <div className="mt-0.5 tide-small" style={{ color: 'var(--tide-ink)', fontWeight: 500 }}>{stateLabel(s.fromState)} <span aria-hidden>&rarr;</span><span className="tide-sr">to</span> {stateLabel(s.toState)}</div>
      <div className="tide-tiny tide-ink-2">
        By {actorLabel(s)} · Landed with <span style={{ color: 'var(--tide-ink)' }}>{landedWith(s)}</span>
        {s.origin !== 'PERSON' && <span className="tide-sr"> ({ORIGIN_LABEL[s.origin]})</span>}
      </div>
    </li>
  );
}

function CurrentList({ items }: { items: Timeline['current'] }) {
  return (
    <ul className="tide-panel" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {items.map((c, i) => (
        <li
          key={`${c.domain}:${c.recordId}`}
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 tide-small"
          style={i > 0 ? { borderTop: '1px solid var(--tide-line)' } : undefined}
        >
          <span className="flex items-center gap-2"><DomainMark domain={c.domain} /><span style={{ fontWeight: 600 }}>{DOMAIN_LABEL[c.domain]}</span><span className="tide-ink-2">{c.recordType.replace(/_/g, ' ')}</span></span>
          <span>{stateLabel(c.state)}</span>
          <span className="tide-ink-2">with {c.ownerName || NOT_RECORDED}{c.since ? ` since ${fmtWhen(c.since)}` : ''}</span>
        </li>
      ))}
    </ul>
  );
}
